// pages/Portfolio.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code,
  ShoppingCart,
  Palette,
  Film,
  Globe,
  Smartphone,
  BarChart,
  ExternalLink,
  Github,
  Eye,
  Filter,
  X,
  Calendar,
  Users,
  Tag,
  Award,
  ChevronRight,
  ArrowRight,
  Star
} from 'lucide-react';

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const projects = [
    {
      id: 1,
      title: 'E-commerce Platform',
      category: 'web',
      type: 'Web Development',
      description: 'A full-featured e-commerce platform with advanced shopping cart, payment integration, and admin dashboard.',
      client: 'FashionHub Retail',
      duration: '8 weeks',
      teamSize: '4 members',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
      features: [
        'Product Management System',
        'Secure Payment Gateway',
        'Inventory Management',
        'Order Tracking',
        'Customer Dashboard'
      ],
      results: {
        revenue: 'Increase by 250%',
        traffic: 'Growth by 180%',
        conversion: 'Boost by 40%'
      },
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      link: 'https://fashionhub.example.com',
      github: 'https://github.com/razatech/fashionhub',
      rating: 4.9,
      testimonials: [
        {
          name: 'Sarah Johnson',
          role: 'CEO, FashionHub',
          comment: 'Exceptional work! The platform transformed our online sales.'
        }
      ]
    },
    {
      id: 2,
      title: 'Corporate Website Redesign',
      category: 'web',
      type: 'WordPress Development',
      description: 'Complete redesign of corporate website with modern UI/UX and improved performance.',
      client: 'TechCorp Global',
      duration: '6 weeks',
      teamSize: '3 members',
      technologies: ['WordPress', 'PHP', 'JavaScript', 'Sass'],
      features: [
        'Responsive Design',
        'Custom Theme Development',
        'SEO Optimization',
        'Multilingual Support',
        'Performance Optimization'
      ],
      results: {
        traffic: 'Increase by 150%',
        engagement: 'Up by 120%',
        leads: 'Growth by 200%'
      },
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      link: 'https://techcorp.example.com',
      rating: 4.8
    },
    {
      id: 3,
      title: 'Mobile Banking App',
      category: 'mobile',
      type: 'Mobile Development',
      description: 'Secure mobile banking application with biometric authentication and real-time transactions.',
      client: 'SecureBank Financial',
      duration: '12 weeks',
      teamSize: '5 members',
      technologies: ['React Native', 'Node.js', 'PostgreSQL', 'AWS'],
      features: [
        'Biometric Authentication',
        'Real-time Transactions',
        'Bill Payments',
        'Investment Tracking',
        '24/7 Support'
      ],
      results: {
        users: '500K+ downloads',
        rating: '4.8 App Store',
        transactions: '2M+ monthly'
      },
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      rating: 4.9
    },
    {
      id: 4,
      title: 'Brand Identity Package',
      category: 'design',
      type: 'Graphic Design',
      description: 'Complete brand identity including logo, color palette, typography, and marketing materials.',
      client: 'Bloom Creative Agency',
      duration: '4 weeks',
      teamSize: '2 members',
      technologies: ['Adobe Illustrator', 'Photoshop', 'InDesign'],
      features: [
        'Logo Design',
        'Brand Guidelines',
        'Business Cards',
        'Social Media Kit',
        'Marketing Templates'
      ],
      results: {
        recognition: 'Increase by 300%',
        consistency: '100% brand unity',
        satisfaction: 'Client rating 5/5'
      },
      image: 'https://images.unsplash.com/photo-1545235617-9465d2a55698?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      rating: 5.0
    },
    {
      id: 5,
      title: 'Product Launch Video',
      category: 'video',
      type: 'Video Editing',
      description: 'Professional product launch video with motion graphics and promotional content.',
      client: 'Innovate Tech Solutions',
      duration: '3 weeks',
      teamSize: '3 members',
      technologies: ['Premiere Pro', 'After Effects', 'DaVinci Resolve'],
      features: [
        '4K Video Editing',
        'Motion Graphics',
        'Sound Design',
        'Color Grading',
        'Multiple Formats'
      ],
      results: {
        views: '1M+ in first month',
        engagement: '500% increase',
        conversion: '300% higher'
      },
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      link: 'https://youtube.com/innovate-product',
      rating: 4.9
    },
    {
      id: 6,
      title: 'Digital Marketing Campaign',
      category: 'marketing',
      type: 'Digital Marketing',
      description: 'Comprehensive digital marketing campaign across social media, search, and email channels.',
      client: 'GrowthStart Inc.',
      duration: 'Ongoing',
      teamSize: '4 members',
      technologies: ['Google Ads', 'Facebook Ads', 'Mailchimp', 'Analytics'],
      features: [
        'SEO Optimization',
        'Social Media Strategy',
        'PPC Campaigns',
        'Email Marketing',
        'Analytics Dashboard'
      ],
      results: {
        roi: '450% return',
        leads: '5000+ generated',
        sales: 'Increase by 200%'
      },
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      rating: 4.7
    },
    {
      id: 7,
      title: 'SaaS Dashboard',
      category: 'web',
      type: 'Frontend Development',
      description: 'Advanced analytics dashboard for SaaS platform with real-time data visualization.',
      client: 'DataInsight Corp',
      duration: '10 weeks',
      teamSize: '3 members',
      technologies: ['React', 'TypeScript', 'D3.js', 'Chart.js'],
      features: [
        'Real-time Analytics',
        'Custom Dashboards',
        'Export Functionality',
        'User Management',
        'API Integration'
      ],
      results: {
        efficiency: 'Improve by 60%',
        users: '10K+ active',
        satisfaction: '4.9/5 rating'
      },
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      github: 'https://github.com/razatech/dashboard',
      rating: 4.9
    },
    {
      id: 8,
      title: 'Restaurant Booking System',
      category: 'web',
      type: 'Full Stack Development',
      description: 'Complete restaurant management system with online booking, menu, and order management.',
      client: 'Gourmet Dining Group',
      duration: '9 weeks',
      teamSize: '4 members',
      technologies: ['Vue.js', 'Python', 'Django', 'PostgreSQL'],
      features: [
        'Online Reservations',
        'Menu Management',
        'Order Processing',
        'Table Management',
        'Analytics'
      ],
      results: {
        bookings: 'Increase by 300%',
        revenue: 'Grow by 180%',
        efficiency: 'Improve by 70%'
      },
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      rating: 4.8
    }
  ];

  const categories = [
    { id: 'all', label: 'All Projects', count: projects.length, icon: <Eye /> },
    { id: 'web', label: 'Web Development', count: projects.filter(p => p.category === 'web').length, icon: <Code /> },
    { id: 'mobile', label: 'Mobile Apps', count: projects.filter(p => p.category === 'mobile').length, icon: <Smartphone /> },
    { id: 'design', label: 'Design', count: projects.filter(p => p.category === 'design').length, icon: <Palette /> },
    { id: 'video', label: 'Video', count: projects.filter(p => p.category === 'video').length, icon: <Film /> },
    { id: 'marketing', label: 'Marketing', count: projects.filter(p => p.category === 'marketing').length, icon: <BarChart /> }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);

  const ProjectModal = ({ project, onClose }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{project.title}</h2>
            <p className="text-gray-600">{project.type}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Project Image */}
          <div className="mb-6">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-64 md:h-80 object-cover rounded-xl"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="md:col-span-2 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Project Overview</h3>
                <p className="text-gray-700">{project.description}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Key Features</h3>
                <ul className="grid grid-cols-2 gap-3">
                  {project.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <ChevronRight className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Technologies Used</h3>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Stats */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Project Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-500 mr-3" />
                    <div>
                      <div className="text-sm text-gray-600">Duration</div>
                      <div className="font-medium">{project.duration}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-gray-500 mr-3" />
                    <div>
                      <div className="text-sm text-gray-600">Team Size</div>
                      <div className="font-medium">{project.teamSize}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Tag className="w-5 h-5 text-gray-500 mr-3" />
                    <div>
                      <div className="text-sm text-gray-600">Client</div>
                      <div className="font-medium">{project.client}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Results Achieved</h3>
                <div className="space-y-3">
                  {Object.entries(project.results).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-gray-600 capitalize">{key}</span>
                      <span className="font-semibold text-green-600">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Client Rating</h3>
                <div className="flex items-center mb-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.floor(project.rating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 font-bold">{project.rating}/5</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {project.link && (
                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
                  >
                    <ExternalLink className="mr-2" size={20} />
                    Live Preview
                  </motion.a>
                )}
                {project.github && (
                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-900 transition"
                  >
                    <Github className="mr-2" size={20} />
                    View Code
                  </motion.a>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our <span className="text-purple-200">Portfolio</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Explore our successful projects that showcase our expertise and 
              commitment to delivering exceptional results.
            </p>
            <div className="flex justify-center items-center space-x-4">
              <div className="flex items-center">
                <Award className="w-6 h-6 mr-2" />
                <span className="font-semibold">150+ Projects Completed</span>
              </div>
              <div className="h-4 w-px bg-white/30"></div>
              <div className="flex items-center">
                <Star className="w-6 h-6 mr-2" />
                <span className="font-semibold">4.8/5 Average Rating</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Controls Section */}
      <section className="py-8 bg-white border-b sticky top-20 z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setFilter(category.id)}
                  className={`flex items-center px-4 py-2 rounded-full font-medium transition-all ${
                    filter === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.label}
                  <span className="ml-2 text-sm opacity-75">
                    ({category.count})
                  </span>
                </motion.button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-700 font-medium">View:</span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 rounded-md transition ${
                    viewMode === 'grid'
                      ? 'bg-white text-blue-600 shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded-md transition ${
                    viewMode === 'list'
                      ? 'bg-white text-blue-600 shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  List
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={viewMode}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-8' : 'space-y-6'}
            >
              {filteredProjects.map((project) => (
                viewMode === 'grid' ? (
                  <motion.div
                    key={project.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    whileHover={{ y: -10 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100"
                  >
                    {/* Project Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-semibold">
                          {project.type}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 flex items-center">
                        <div className="flex items-center bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <Star className="w-4 h-4 text-yellow-500 mr-1" />
                          <span className="font-semibold">{project.rating}</span>
                        </div>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-gray-900">{project.title}</h3>
                        <span className="text-sm text-gray-500">{project.duration}</span>
                      </div>
                      
                      <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-6">
                        {project.technologies.slice(0, 3).map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                        {project.technologies.length > 3 && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            +{project.technologies.length - 3}
                          </span>
                        )}
                      </div>

                      {/* Results Preview */}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        {Object.entries(project.results).slice(0, 2).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-lg font-bold text-green-600">{value}</div>
                            <div className="text-xs text-gray-500 capitalize">{key}</div>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedProject(project)}
                          className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center"
                        >
                          <Eye className="mr-2" size={18} />
                          View Details
                        </motion.button>
                        {project.link && (
                          <motion.a
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center"
                          >
                            <ExternalLink size={18} />
                          </motion.a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  // List View
                  <motion.div
                    key={project.id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    whileHover={{ x: 5 }}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100"
                  >
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Project Image */}
                      <div className="md:w-1/4">
                        <div className="relative h-48 md:h-full rounded-xl overflow-hidden">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      {/* Project Details */}
                      <div className="md:w-3/4">
                        <div className="flex flex-col md:flex-row justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                            <div className="flex items-center gap-4 mb-3">
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                {project.type}
                              </span>
                              <div className="flex items-center">
                                <Star className="w-4 h-4 text-yellow-500 mr-1" />
                                <span className="font-semibold">{project.rating}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <Calendar className="w-4 h-4 mr-1" />
                                <span className="text-sm">{project.duration}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500 mb-1">Client</div>
                            <div className="font-semibold">{project.client}</div>
                          </div>
                        </div>

                        <p className="text-gray-600 mb-4">{project.description}</p>

                        <div className="flex flex-wrap gap-2 mb-6">
                          {project.technologies.map((tech, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>

                        <div className="flex justify-between items-center">
                          <div className="grid grid-cols-3 gap-4">
                            {Object.entries(project.results).slice(0, 3).map(([key, value]) => (
                              <div key={key} className="text-center">
                                <div className="text-lg font-bold text-green-600">{value}</div>
                                <div className="text-xs text-gray-500 capitalize">{key}</div>
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex gap-3">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setSelectedProject(project)}
                              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
                            >
                              View Details
                            </motion.button>
                            {project.link && (
                              <motion.a
                                whileHover={{ scale: 1.05 }}
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center"
                              >
                                <ExternalLink size={18} />
                              </motion.a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              ))}
            </motion.div>
          </AnimatePresence>

          {/* No Results */}
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Filter className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-600">
                Try selecting a different category to view more projects.
              </p>
              <button
                onClick={() => setFilter('all')}
                className="mt-6 text-blue-600 font-semibold hover:text-blue-700"
              >
                View all projects
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { value: '150+', label: 'Projects Completed', icon: <Award /> },
              { value: '98%', label: 'Client Satisfaction', icon: <Star /> },
              { value: '50+', label: 'Happy Clients', icon: <Users /> },
              { value: '24/7', label: 'Support Available', icon: <Calendar /> }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Let's create something amazing together. Share your vision with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Start Free Consultation
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition"
              >
                View Case Studies
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Portfolio;